"use client";

import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { enUS, te } from "date-fns/locale";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";

import CustomImage from "@/components/shared/CustomImage";
import SocialShare from "@/components/shared/SocialShare";
import { Typography } from "@/components/shared/Typography";
import { getLabel } from "@/data/labels";
import { PHOTO_STORY_PAGE_LIMIT } from "@/data/constants";
import { fetchPhotoStories } from "@/services/photoStoriesService";
import { PhotoStoryType } from "@/types/photoStory";
import { getImageUrl } from "@/utils/getImageUrl";
import { getLocaleFromPathname } from "@/utils/getLocaleFromPath";
import { IoMdShare } from "react-icons/io";

interface PhotoStoryListSectionProps {
  initialData: {
    docs: PhotoStoryType[];
    totalDocs: number;
  };
  lang: AppLocale;
}

export default function PhotoStoryListSection({ initialData, lang }: PhotoStoryListSectionProps) {
  const pathname = usePathname();
  const locale = getLocaleFromPathname(pathname) || "te-IN";
  const [photoStories, setPhotoStories] = useState<PhotoStoryType[]>(initialData?.docs || []);
  const [filters, setFilters] = useState({
    limit: PHOTO_STORY_PAGE_LIMIT,
    page: 1,
  });

  const { data, error, isFetching } = useQuery({
    queryKey: ["fetchPhotoStories", filters.page],
    queryFn: () => fetchPhotoStories({ limit: filters?.limit, page: filters?.page, lang }),
    enabled: filters.page > 1,
  });

  const handleLoadMore = () => {
    setFilters(prev => ({ ...prev, page: prev.page + 1 }));
  };

  useEffect(() => {
    if (data) {
      setPhotoStories(prev => [...prev, ...(data?.docs || [])]);
    }
  }, [data]);

  // Determine if we should show the Load More button
  const showLoadMore =
    photoStories?.length > 0 &&
    ((filters.page === 1 && initialData.totalDocs > photoStories.length) ||
      (data && data?.totalDocs > photoStories.length));

  // useEffect(() => {
  //   setPhotoStories(initialData?.docs || []);
  // }, [initialData]);

  return (
    <div className="">
      <div className="grid grid-cols-2 gap-4 md:gap-8 ">
        {photoStories?.map((photoStory, index) => {
          return (
            <div key={index} className="group w-full ">
              <Link
                href={`/${locale}/photo-stories/news-in-pics/${photoStory?.slug}`}
                aria-label={`View photo story: ${photoStory?.title}`}
              >
                <div className="relative w-full overflow-hidden rounded-md aspect-video">
                  <CustomImage
                    src={getImageUrl(
                      photoStory?.thumbnail?.sizes?.sd?.url || photoStory?.thumbnail?.url,
                    )}
                    alt={photoStory?.thumbnail?.alt || ""}
                    className="object-cover w-full h-full img-zoom-hover"
                    phImg="/images/placeholder-grey-sd.png"
                  />
                </div>
              </Link>

              <Link
                href={`/${locale}/photo-stories/news-in-pics/${photoStory?.slug}`}
                aria-label={`View photo story: ${photoStory?.title}`}
              >
                <Typography variant="h4" language={lang} className="mt-4 line-clamp-2">
                  {photoStory?.title}
                </Typography>
              </Link>

              <div className="hidden md:flex gap-4 mt-3 uppercase divide-x-2 divide-black dark:divide-white">
                <Typography className="font-medium" variant="body2">
                  {getLabel(lang, "PHOTO_STORIES_PAGE")}
                </Typography>
                <Typography
                  className="pl-4 text-secondaryText dark:text-darkBgSecondaryColor"
                  variant="body2"
                >
                  {getLabel(lang, "NEWS_IN_PICS")}
                </Typography>
              </div>

              <div className="flex  items-center justify-between gap-4 mt-4">
                <span className="block text-[#858585] text-xs md:text-sm uppercase">
                  {format(new Date(photoStory?.createdAt), "MMMM dd, yyyy", {
                    locale: lang === "te" ? te : enUS,
                  })}
                </span>

                <div className=" hidden md:block">
                  <SocialShare
                    title={photoStory?.title}
                    url={`/${locale}/photo-stories/news-in-pics/${photoStory?.slug}`}
                  />
                </div>
                <div className="md:hidden">
                  <SocialShare
                    customButton={
                      <button
                        className="flex items-center justify-center text-gray-600 dark:text-white"
                        aria-label="Share"
                      >
                        <IoMdShare />
                      </button>
                    }
                    title={photoStory?.title}
                    url={`/photo-stories/news-in-pics/${photoStory?.slug}`}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {showLoadMore && (
        <div className="flex items-center justify-center mt-8">
          <button
            className="load-more-btn"
            onClick={handleLoadMore}
            disabled={isFetching}
            aria-label={getLabel(lang, "LOAD_MORE")}
          >
            {getLabel(lang, "LOAD_MORE")}
          </button>
        </div>
      )}
    </div>
  );
}
